import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

export default function CompanyDetail() {
    // Redirect to login page if DevTools or View Source shortcuts are pressed
    useEffect(() => {
      const handleKeyDown = (e) => {
        // F12
        if (e.key === 'F12') {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+Shift+I or Cmd+Opt+I
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+U or Cmd+U
        if ((e.ctrlKey || e.metaKey) && (e.key === 'U' || e.key === 'u')) {
          window.location.href = '/login';
          e.preventDefault();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
  const { cin } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!can('mgt7a')) {
      setError('locked');
      setLoading(false);
      return;
    }
    api.companies.detail(cin)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [cin]);

  if (loading) return <div className="page-loading"><div className="spinner" /><span>Loading company data…</span></div>;

  if (error === 'locked') return (
    <div className="page">
      <div className="detail-locked">
        <div className="lock-icon">🔒</div>
        <h2>Restricted Access</h2>
        <p>Full company details are only available to analysts and administrators.</p>
        <p>You can view company contact status on the <strong>Companies</strong> page.</p>
        <button className="btn-secondary" onClick={() => nav('/companies')}>← Back to Companies</button>
      </div>
    </div>
  );

  if (error) return <div className="page"><div className="page-error">Error: {error}</div></div>;
  if (!data) return null;

  const { company: c, gst, iec, contacts } = data;

  const fmt = v => (v == null ? '—' : String(v));
  const fmtDate = v => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtMoney = v => v == null ? '—' : `₹ ${Number(v).toLocaleString('en-IN')}`;

  // Disable right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  return (
    <div className="page" onContextMenu={handleContextMenu}>
      <div className="page-header">
        <div>
          <button className="back-btn" onClick={() => nav(-1)}>← Back</button>
          <h1 className="page-title" style={{ marginTop: '0.5rem' }}>{c?.COMPANY_NAME || cin}</h1>
          <p className="mono" style={{ color: 'var(--text-muted)', marginTop: '2px' }}>{cin}</p>
        </div>
        {c?.COMPANY_STATUS && (
          <span className={c.COMPANY_STATUS === 'Active' ? 'badge-yes' : 'badge-no'} style={{ alignSelf: 'flex-start' }}>
            {c.COMPANY_STATUS}
          </span>
        )}
      </div>

      {/* ── Core Details ────────────────────────────────────── */}
      <section className="detail-section">
        <h2 className="section-title">Company Details</h2>
        <div className="detail-grid">
          <div className="detail-field"><label>CIN</label><span className="mono">{fmt(c?.CIN)}</span></div>
          <div className="detail-field"><label>Company Class</label><span>{fmt(c?.COMPANY_CLASS)}</span></div>
          <div className="detail-field"><label>Company Type</label><span>{fmt(c?.COMPANY_TYPE)}</span></div>
          <div className="detail-field"><label>Registration Date</label><span>{fmtDate(c?.REGISTRATION_DATE)}</span></div>
          <div className="detail-field"><label>ROC</label><span>{fmt(c?.ROC_NAME)}</span></div>
          <div className="detail-field"><label>RD Region</label><span>{fmt(c?.RD_NAME_REGION)}</span></div>
          <div className="detail-field"><label>Authorised Capital</label><span>{fmtMoney(c?.AUTHORISED_CAPITAL)}</span></div>
          <div className="detail-field"><label>Paid-up Capital</label><span>{fmtMoney(c?.PAID_UP_CAPITAL)}</span></div>
          <div className="detail-field"><label>Email</label><span>{fmt(c?.EMAIL_ID)}</span></div>
          <div className="detail-field"><label>Phone</label><span>{fmt(c?.PHONE)}</span></div>
          <div className="detail-field"><label>PAN</label><span className="mono">{fmt(c?.PAN)}</span></div>
          <div className="detail-field">
            <label>INC-20A Status</label>
            <span>{c?.INC_20A_STATUS
              ? <span className="status-badge" style={{
                  background: '#0F4C8122',
                  color: '#0F4C81',
                  border: '1px solid #0F4C81'
                }}>{c.INC_20A_STATUS}</span>
              : '—'}
            </span>
          </div>
          <div className="detail-field" style={{ gridColumn: 'span 3' }}><label>Registered Address</label><span>{fmt(c?.REGISTERED_ADDRESS)}</span></div>
        </div>
      </section>

      {/* ── GST Records ─────────────────────────────────────── */}
      <section className="detail-section">
        <h2 className="section-title">GST Records ({gst?.length || 0})</h2>
        {gst && gst.length > 0 ? (
          <div className="table-card">
            <div className="table-scroll">
              <table className="data-table">
                <tbody>
                  {gst.map(g => (
                    <tr key={g.GSTIN} className="gst-detail-row">
                      <td colSpan="6" style={{ padding: 0 }}>
                        <details style={{ background: '#f8fafc', padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                            <span className="mono">{g.GSTIN}</span> — {g.LEGAL_NAME || '—'} ({g.TABLE_STATE || '—'}) 
                            <span className={g.GSTIN_STATUS === 'Active' ? 'badge-yes' : 'badge-no'} style={{ marginLeft: '1rem' }}>{g.GSTIN_STATUS}</span>
                          </summary>
                          <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div><strong>Trade Name:</strong> {g.TRADE_NAME || '—'}</div>
                            <div><strong>Taxpayer Type:</strong> {g.TAXPAYER_TYPE || '—'}</div>
                            <div><strong>Constitution:</strong> {g.CONSTITUTION_OF_BUSINESS || '—'}</div>
                            <div><strong>Effective Date:</strong> {fmtDate(g.EFFECTIVE_REG_DATE) || '—'}</div>
                            <div><strong>Core Business:</strong> {g.NATURE_CORE_BUSINESS || '—'}</div>
                            <div><strong>Table Status:</strong> {g.TABLE_STATUS || '—'}</div>
                            <div style={{ gridColumn: '1 / -1' }}><strong>Address:</strong><br/>{g.PRINCIPAL_PLACE_ADDRESS || '—'}</div>
                            <div style={{ gridColumn: '1 / -1' }}><strong>Business Activities:</strong><br/>{g.NATURE_BUSINESS_ACTIVITIES || '—'}</div>
                            <div style={{ gridColumn: '1 / -1' }}><strong>Goods & Services:</strong><br/>{g.GOODS_AND_SERVICES || '—'}</div>
                          </div>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="empty-state">No GST records found for this PAN.</div>
        )}
      </section>

      {/* ── IEC Records ─────────────────────────────────────── */}
      <section className="detail-section">
        <h2 className="section-title">IEC / SIMS Records ({iec?.length || 0})</h2>
        {iec && iec.length > 0 ? (
          <div className="table-card">
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>IEC Name</th><th>Mobile</th><th>Email</th><th>Extracted</th>
                  </tr>
                </thead>
                <tbody>
                  {iec.map((s, i) => (
                    <tr key={i}>
                      <td>{s.IEC_NAME || '—'}</td>
                      <td>{s.IEC_MOBILE || '—'}</td>
                      <td>{s.IEC_EMAIL || '—'}</td>
                      <td className="date-cell">{fmtDate(s.EXTRACTION_DATE)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="empty-state">No IEC / SIMS records found for this PAN.</div>
        )}
      </section>

      {/* ── Contact History ──────────────────────────────────── */}
      <section className="detail-section">
        <h2 className="section-title">Contact History ({contacts?.length || 0})</h2>
        {contacts && contacts.length > 0 ? (
          <div className="contact-history">
            {contacts.map((c, i) => (
              <div key={i} className="contact-entry">
                <div className="contact-meta">
                  <span className="contact-by">{c.CONTACTED_BY}</span>
                  <span className="contact-date">{fmtDate(c.CONTACTED_AT)}</span>
                </div>
                {c.NOTE && <p className="contact-note">{c.NOTE}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">No contact records yet.</div>
        )}
      </section>
    </div>
  );
}
